/**
 * Signal triage board (CONTRACT_V2 §9 / upd-1 §9.2).
 *
 * Renders all signals as a kanban-style board, one column per `SIGNAL_STATUS`
 * stage (new → triaged → clustered → candidate_object → converted_* → ignored /
 * archived). From a card a triager can:
 *   - advance the signal's status (PATCH /api/signals/:id) — the worker emits
 *     `signal.triaged` when status→triaged;
 *   - convert the signal to evidence (POST /api/evidence with
 *     `source_signal_id` set). The signal's move to `converted_to_evidence` is
 *     performed by the signals-slice backend rule/route hook (CONTRACT_V2 §9),
 *     NOT hard-coded here; the board simply re-fetches to pick it up.
 *
 * Reads via Refine's `useList`; writes via the shared `useApiAction` helper
 * (native fetch + cache invalidation), mirroring the D5 special-action pattern.
 * The page renders through the shared `PageHeader` + canonical
 * loading/error/empty states (design-system §4.1 / §4.4) and status reads via
 * `StatusDot`/`StatusBadge` so the board tracks the rest of the portal.
 */
import { useMemo } from "react";
import { useList, useNavigation } from "@refinedev/core";
import { Icon, RefreshCw } from "@/components/ui/icon";
import {
  SIGNAL_STATUS,
  type Signal,
  type SignalStatus,
} from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { StatusDot } from "@/components/app/StatusBadge";
import { humanize } from "@/lib/format";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/ui/states";
import { useApiAction } from "../_shared/useApiAction";
import { formatDateTime } from "../_shared/runs-ui";

/** Linear pipeline used to compute the "advance" target for a signal. */
const PIPELINE: readonly SignalStatus[] = [
  "new",
  "triaged",
  "clustered",
  "candidate_object",
] as const;

/** Human label for a status column header. */
function statusLabel(status: SignalStatus): string {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Next pipeline status, or null if the signal is at/after the last stage. */
function nextStatus(status: SignalStatus): SignalStatus | null {
  const idx = PIPELINE.indexOf(status);
  if (idx === -1 || idx >= PIPELINE.length - 1) return null;
  return PIPELINE[idx + 1] ?? null;
}

function scoreLabel(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return value.toFixed(2);
}

function SignalCard({ signal }: { signal: Signal }) {
  const { show } = useNavigation();
  const { run, loading } = useApiAction();
  const advanceTarget = nextStatus(signal.status);
  const canConvert =
    signal.status !== "converted_to_evidence" &&
    signal.status !== "converted_to_package_proposal" &&
    signal.status !== "archived" &&
    signal.status !== "ignored";

  const advance = async () => {
    if (!advanceTarget) return;
    await run(`/signals/${signal.id}`, {
      method: "PATCH",
      body: { status: advanceTarget },
      invalidateResources: ["signals"],
      successMessage: `Signal moved to ${statusLabel(advanceTarget)}.`,
    });
  };

  const convertToEvidence = async () => {
    await run(`/evidence`, {
      method: "POST",
      body: {
        title: signal.title,
        evidence_type: signal.signal_type,
        body: signal.body ?? undefined,
        polarity: "supports",
        source_signal_id: signal.id,
        confidence_score: signal.confidence_score ?? undefined,
      },
      // signal status flips to converted_to_evidence via the backend hook
      invalidateResources: ["signals", "evidence"],
      successMessage: "Signal converted to evidence.",
    });
  };

  return (
    <Card>
      <CardContent className="space-y-2.5 p-3">
        <button
          type="button"
          className="text-left text-sm font-medium leading-snug hover:underline"
          onClick={() => show("signals", signal.id)}
        >
          {signal.title}
        </button>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary">{humanize(signal.signal_type)}</Badge>
          <span className="text-xs text-muted-foreground">
            str {scoreLabel(signal.strength)} · conf{" "}
            {scoreLabel(signal.confidence_score)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatDateTime(signal.created_at)}
        </p>
        {advanceTarget || canConvert ? (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {advanceTarget ? (
              <Button
                size="sm"
                variant="outline"
                disabled={loading}
                onClick={() => void advance()}
              >
                <Icon name="next" size={14} className="mr-1" aria-hidden />
                {statusLabel(advanceTarget)}
              </Button>
            ) : null}
            {canConvert ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={loading}
                onClick={() => void convertToEvidence()}
              >
                <Icon name="evidence" size={14} className="mr-1" aria-hidden />
                To evidence
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StatusColumn({
  status,
  signals,
}: {
  status: SignalStatus;
  signals: Signal[];
}) {
  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg border border-border bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="flex min-w-0 items-center gap-2">
          <StatusDot status={status} />
          <span className="truncate text-sm font-semibold">
            {statusLabel(status)}
          </span>
        </span>
        <Badge variant="secondary">{signals.length}</Badge>
      </div>
      <ScrollArea className="h-[60vh]">
        <div className="space-y-2 p-2">
          {signals.length === 0 ? (
            <p className="px-1 py-6 text-center text-xs text-muted-foreground">
              No signals here.
            </p>
          ) : (
            signals.map((signal) => (
              <SignalCard key={signal.id} signal={signal} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export function SignalTriage() {
  const { create } = useNavigation();
  const { data, isLoading, isError, error, refetch } = useList<Signal>({
    resource: "signals",
    pagination: { pageSize: 200, mode: "server" },
    sorters: [{ field: "created_at", order: "desc" }],
  });

  const signals = data?.data ?? [];

  const byStatus = useMemo(() => {
    const groups = new Map<SignalStatus, Signal[]>();
    for (const status of SIGNAL_STATUS) groups.set(status, []);
    for (const signal of signals) {
      const bucket = groups.get(signal.status);
      if (bucket) bucket.push(signal);
      else groups.set(signal.status, [signal]);
    }
    return groups;
  }, [signals]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Signal triage"
        subtitle="Move raw signals through the pipeline and convert them to evidence."
        icon="signals"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void refetch();
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={() => create("signals")}>
              <Icon name="create" size={16} className="mr-2" aria-hidden />
              New signal
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : signals.length === 0 ? (
        <EmptyState
          icon="signals"
          title="No signals to triage"
          hint="Capture a signal and it will appear here for triage."
          action={
            <Button onClick={() => create("signals")}>
              <Icon name="create" size={16} className="mr-2" aria-hidden />
              New signal
            </Button>
          }
        />
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {SIGNAL_STATUS.map((status) => (
            <StatusColumn
              key={status}
              status={status}
              signals={byStatus.get(status) ?? []}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SignalTriage;
